import type { HabitMeasure } from './habitMeasure'
import { isSimilarTitle, sharedTitleWords } from './similarTitle'

export type LiveKind = 'goal' | 'task' | 'event' | 'habit'

// What a detailed preview carries beyond kind + title (older backends send neither).
type LiveDetails = {
  category: string
  when: 'today' | 'tomorrow' | 'thisWeek' | null
  period: 'daily' | 'weekday' | 'weekend' | 'weekly' | 'days' | null
  days?: number[]
  timeOfDay: 'morning' | 'afternoon' | 'evening' | null
  dueDate: string | null
  time: string | null
  durationMinutes: number | null
  location: string | null
  allDay?: boolean
  measure?: HabitMeasure
  oneOff?: boolean
}

export type PreviewItem = { kind: LiveKind; title: string } & Partial<LiveDetails>
export type LiveItem = PreviewItem & { key: string }

type Titled = { kind: LiveKind; title: string }

// For each `next` item, the index of the `prev` item it continues (or -1). Same kind
// and similar title first, then a similar title whose kind changed (task → habit),
// then — among the first `positional` prev items — the same slot and kind sharing a
// word ("call mum" → "call mum about the trip").
function pairUp(prev: Titled[], next: Titled[], positional: number): number[] {
  const taken = new Set<number>()
  const out = next.map(() => -1)
  const pass = (ok: (p: Titled, n: Titled, pi: number, ni: number) => boolean) =>
    next.forEach((n, ni) => {
      if (out[ni] >= 0) return
      const pi = prev.findIndex((p, i) => !taken.has(i) && ok(p, n, i, ni))
      if (pi < 0) return
      taken.add(pi)
      out[ni] = pi
    })
  pass((p, n) => p.kind === n.kind && isSimilarTitle(p.title, n.title))
  pass((p, n) => isSimilarTitle(p.title, n.title))
  pass(
    (p, n, pi, ni) =>
      pi < positional && pi === ni && p.kind === n.kind && sharedTitleWords(p.title, n.title) > 0,
  )
  return out
}

const keyNumber = (key: string) => Number(key.split(':')[1]) || 0

// A detailed preview item also carries `similar` (always null) and a related-event
// index into the preview list; neither means anything once items are re-keyed.
type RawPreviewItem = PreviewItem & { similar?: unknown; relatedEventIndex?: number }

export type LiveUpdate = { items: LiveItem[]; gone: LiveItem[]; fresh: string[] }

// Each preview re-reads the whole transcript, so the model may reword an item, change
// its kind, or drop it for one pass and bring it back. Carry keys over so the UI
// updates in place; `gone` remembers recently dropped items so a returning one keeps
// its key. `fresh` holds keys of genuinely new items (for the "detected" cue).
export function updateLiveItems(
  prev: LiveItem[],
  gone: LiveItem[],
  next: RawPreviewItem[],
): LiveUpdate {
  const pool = [...prev, ...gone]
  let n = pool.reduce((m, p) => Math.max(m, keyNumber(p.key)), 0)
  const match = pairUp(pool, next, prev.length)
  const fresh: string[] = []
  const items = next.map(({ similar: _s, relatedEventIndex: _r, ...it }, i) => {
    const m = match[i]
    if (m >= 0) return { ...it, key: pool[m].key }
    n += 1
    const key = `live:${n}`
    fresh.push(key)
    return { ...it, key }
  })
  const kept = new Set(items.map((i) => i.key))
  return { items, gone: pool.filter((p) => !kept.has(p.key)).slice(0, 10), fresh }
}

export function mergeLiveItems(prev: LiveItem[], next: RawPreviewItem[]): LiveItem[] {
  return updateLiveItems(prev, [], next).items
}

// The live card each final item continues, so review cards can pick up where the
// live ones left off instead of popping in again.
export function liveKeysFor(live: LiveItem[], items: Titled[]): (string | undefined)[] {
  const match = pairUp(live, items, live.length)
  return match.map((m) => (m >= 0 ? live[m].key : undefined))
}

// A live item as a review item. Fallback when the final extraction comes back empty
// or fails (details the preview didn't have are left neutral for the user to check),
// and how live cards are drawn with the review card.
export function liveToCaptureItem({ key: _key, ...item }: LiveItem) {
  return {
    category: 'inbox',
    when: null,
    period: null,
    timeOfDay: null,
    dueDate: null,
    time: null,
    durationMinutes: null,
    location: null,
    ...item,
    similar: null,
  } satisfies LiveDetails & Titled & { similar: null }
}
